import { useReferencedProperties } from 'src/modules/shared/hooks/api/catalog/use-referenced-properties';
import { PropertyCode } from 'src/modules/shared/components/atoms/property-code';
import { FunnelStatusTerminalOutcome } from 'src/api/generated';
import { FunnelStatusName } from 'src/modules/shared/components/atoms/funnel-status-name';
import { Card } from 'src/modules/shared/components/atoms/cards';
import {
  type ReportDashboardWidgetInstance,
  type ReportFunnelStatusFunnelResponse,
  type ReportFunnelStatusTimeseriesResponse,
  type ReportPropertyInterestRankingResponse,
  type ReportPropertyTypeDistributionResponse,
  type ReportUserAssignmentRankingResponse,
} from 'src/api/generated';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  PiChartBar,
  PiFunnel,
  PiHouseLine,
  PiRows,
  PiTable,
} from 'react-icons/pi';
import type { ReportWidgetQueryData } from '../hooks/reports-dashboard';
import { getSeriesColor } from './reports-dashboard.utils';
import { getReportBreakdownColorClass } from '../utils/report-breakdown-colors.util';
import {
  isFunnelStatusFunnelWidget,
  isFunnelStatusTimeseriesWidget,
  isPropertyInterestRankingWidget,
  isPropertyTypeDistributionWidget,
  isUserAssignmentRankingWidget,
} from '../utils/report-dashboard';

const isFunnelStatusFunnelResponse = (
  response: ReportWidgetQueryData | undefined
): response is ReportFunnelStatusFunnelResponse =>
  response === undefined ||
  ('rows' in response &&
    response.rows.every((row) => 'status' in row && 'percentage' in row));

const isFunnelStatusTimeseriesResponse = (
  response: ReportWidgetQueryData | undefined
): response is ReportFunnelStatusTimeseriesResponse =>
  response === undefined ||
  ('buckets' in response &&
    response.buckets.every((bucket) => 'bucketStart' in bucket));

const isPropertyTypeDistributionResponse = (
  response: ReportWidgetQueryData | undefined
): response is ReportPropertyTypeDistributionResponse =>
  response === undefined ||
  ('rows' in response &&
    response.rows.every((row) => 'type' in row && 'percentage' in row));

const isPropertyInterestRankingResponse = (
  response: ReportWidgetQueryData | undefined
): response is ReportPropertyInterestRankingResponse =>
  response === undefined ||
  ('rows' in response &&
    response.rows.every((row) => 'propertyId' in row && 'label' in row));

const isUserAssignmentRankingResponse = (
  response: ReportWidgetQueryData | undefined
): response is ReportUserAssignmentRankingResponse =>
  response === undefined ||
  ('statuses' in response &&
    Array.isArray(response.statuses) &&
    'rows' in response);

/**
 * Chooses the widget renderer for a query response.
 */
export const WidgetQueryRenderer: React.FC<{
  widget: ReportDashboardWidgetInstance;
  response: ReportWidgetQueryData | undefined;
  onOpenProperty: (propertyId: number) => void;
}> = ({ widget, response, onOpenProperty }) => {
  if (isFunnelStatusFunnelWidget(widget)) {
    if (!isFunnelStatusFunnelResponse(response)) {
      return null;
    }
    return <FunnelStatusFunnelWidget response={response} />;
  }

  if (isFunnelStatusTimeseriesWidget(widget)) {
    if (!isFunnelStatusTimeseriesResponse(response)) {
      return null;
    }
    return <FunnelStatusTimeseriesWidget response={response} />;
  }

  if (isPropertyInterestRankingWidget(widget)) {
    if (!isPropertyInterestRankingResponse(response)) {
      return null;
    }
    return (
      <PropertyInterestRankingWidget
        response={response}
        onOpenProperty={onOpenProperty}
      />
    );
  }

  if (isUserAssignmentRankingWidget(widget)) {
    if (!isUserAssignmentRankingResponse(response)) {
      return null;
    }
    return <UserAssignmentRankingWidget response={response} />;
  }

  if (isPropertyTypeDistributionWidget(widget)) {
    if (!isPropertyTypeDistributionResponse(response)) {
      return null;
    }
    return <PropertyTypeDistributionWidget response={response} />;
  }

  return null;
};

const buildBreakdownLegend = (
  breakdownGroups: {
    key: string;
    label: string;
    count: number;
  }[][]
) => {
  const countsByKey = new Map<string, { label: string; count: number }>();

  for (const breakdown of breakdownGroups) {
    for (const item of breakdown) {
      const current = countsByKey.get(item.key) ?? {
        label: item.label,
        count: 0,
      };
      countsByKey.set(item.key, {
        label: item.label,
        count: current.count + item.count,
      });
    }
  }

  return Array.from(countsByKey.entries())
    .sort((left, right) => {
      if (right[1].count !== left[1].count) {
        return right[1].count - left[1].count;
      }

      return left[1].label.localeCompare(right[1].label, undefined, {
        sensitivity: 'base',
      });
    })
    .map(([key, value]) => ({
      key,
      label: value.label,
      count: value.count,
      colorClass: getReportBreakdownColorClass(key, value.label),
    }));
};

const FunnelStatusFunnelWidget: React.FC<{
  response: ReportFunnelStatusFunnelResponse | undefined;
}> = ({ response }) => {
  const { t } = useI18n();
  const rows = response?.rows ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title={t('reports.dashboard.empty.noDataTitle')}
        description={t('reports.dashboard.empty.funnelStatusFunnel')}
        icon={<PiFunnel className="h-6 w-6" />}
        size="sm"
      />
    );
  }

  const maxCount = Math.max(...rows.map((row) => row.count), 1);
  const breakdownLegend = buildBreakdownLegend(
    rows.map((row) => row.breakdown)
  );
  const hasBreakdown = breakdownLegend.length > 0;

  return (
    <div className="space-y-4">
      {response?.outcomeTotals && (
        <div
          className="space-y-3"
          aria-label={t('reports.dashboard.outcomes.label')}
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Card
              variant="success"
              appearance="soft"
              shadow="none"
              className="border border-success-200 p-4"
            >
              <FunnelStatusName
                name={t('reports.dashboard.outcomes.positive')}
                outcome={FunnelStatusTerminalOutcome.POSITIVE}
                icon="outcome"
                weight="semibold"
              />
              <Text size="h3" weight="semibold" className="text-success-800">
                {response.outcomeTotals.positive}
              </Text>
            </Card>
            <Card
              variant="secondary"
              appearance="soft"
              shadow="none"
              className="border border-secondary-200 p-4"
            >
              <FunnelStatusName
                name={t('reports.dashboard.outcomes.negative')}
                outcome={FunnelStatusTerminalOutcome.NEGATIVE}
                icon="outcome"
                weight="semibold"
              />
              <Text size="h3" weight="semibold" className="text-secondary-700">
                {response.outcomeTotals.negative}
              </Text>
            </Card>
          </div>
        </div>
      )}
      {rows.map((row) => (
        <div key={row.status.id} className="space-y-2">
          <div className="flex items-center justify-between gap-3 text-sm">
            <FunnelStatusName
              name={row.status.name}
              outcome={row.status.terminalOutcome}
              icon="outcome"
              weight="medium"
            />
            <Text as="span" size="small" className="text-primary-900">
              {row.count} · {row.percentage.toFixed(0)}%
            </Text>
          </div>
          <div className="overflow-hidden rounded-full bg-neutral-100">
            <div
              className={`flex h-4 min-w-[6px] items-stretch rounded-full ${row.status.terminalOutcome === FunnelStatusTerminalOutcome.POSITIVE ? 'bg-success-500' : row.status.terminalOutcome === FunnelStatusTerminalOutcome.NEGATIVE ? 'bg-secondary-400' : 'bg-neutral-700'}`}
              style={{
                width: `${Math.max((row.count / maxCount) * 100, row.count > 0 ? 6 : 0)}%`,
              }}
            >
              {hasBreakdown
                ? row.breakdown.map((bucket) => (
                    <div
                      key={`${row.status.id}-${bucket.key}`}
                      className={`h-full ${getReportBreakdownColorClass(bucket.key, bucket.label)}`}
                      style={{
                        width: `${(bucket.count / row.count) * 100}%`,
                      }}
                      title={`${bucket.label}: ${bucket.count}`}
                    />
                  ))
                : null}
            </div>
          </div>
          {row.breakdown.length > 0 && (
            <div className="flex flex-wrap gap-2 text-xs text-primary-900">
              {row.breakdown.map((bucket) => (
                <div
                  key={bucket.key}
                  className="inline-flex items-center gap-1.5"
                >
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${getReportBreakdownColorClass(bucket.key, bucket.label)}`}
                  />
                  <Text as="span" size="tiny" className="text-primary-900">
                    {bucket.label} ({bucket.count})
                  </Text>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const FunnelStatusTimeseriesWidget: React.FC<{
  response: ReportFunnelStatusTimeseriesResponse | undefined;
}> = ({ response }) => {
  const { t } = useI18n();
  const buckets = response?.buckets ?? [];
  if (buckets.length === 0) {
    return (
      <EmptyState
        title={t('reports.dashboard.empty.noDataTitle')}
        description={t('reports.dashboard.empty.timeseries')}
        icon={<PiChartBar className="h-6 w-6" />}
        size="sm"
      />
    );
  }

  const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);
  const breakdownLegend = buildBreakdownLegend(
    buckets.map((bucket) => bucket.breakdown)
  );
  const hasBreakdown = breakdownLegend.length > 0;

  return (
    <div className="space-y-3">
      <div className="flex h-56 items-stretch gap-2 overflow-x-auto pb-1">
        {buckets.map((bucket) => {
          const totalHeight = Math.max(
            (bucket.count / maxCount) * 100,
            bucket.count > 0 ? 8 : 0
          );
          const breakdownCountByKey = new Map(
            bucket.breakdown.map((item) => [item.key, item.count])
          );

          return (
            <div
              key={bucket.bucketStart}
              className="flex min-w-[64px] flex-1 flex-col items-center gap-2"
            >
              <div className="flex h-5 shrink-0 items-end">
                <Text as="span" size="tiny" className="text-primary-900">
                  {bucket.count}
                </Text>
              </div>
              <div className="flex h-36 w-full items-end pt-3">
                <div className="relative flex h-full w-full items-end rounded-t-xl bg-neutral-100">
                  {hasBreakdown ? (
                    <div
                      className="flex w-full flex-col justify-end overflow-hidden rounded-t-xl"
                      style={{ height: `${totalHeight}%` }}
                    >
                      {breakdownLegend.map((item) => {
                        const count = breakdownCountByKey.get(item.key) ?? 0;
                        if (count === 0 || bucket.count === 0) {
                          return null;
                        }

                        return (
                          <div
                            key={`${bucket.bucketStart}-${item.key}`}
                            className={`w-full ${item.colorClass}`}
                            style={{
                              height: `${(count / bucket.count) * 100}%`,
                            }}
                            title={`${item.label}: ${count}`}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div
                      className="w-full rounded-t-xl bg-neutral-800"
                      style={{ height: `${totalHeight}%` }}
                    />
                  )}
                </div>
              </div>
              <div className="flex h-8 shrink-0 items-start">
                <Text
                  as="span"
                  size="tiny"
                  className="text-center leading-tight text-primary-900"
                >
                  {bucket.label}
                </Text>
              </div>
            </div>
          );
        })}
      </div>
      {hasBreakdown && (
        <div className="flex flex-wrap gap-2 text-xs text-primary-900">
          {breakdownLegend.map((item) => (
            <div key={item.key} className="inline-flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-full ${item.colorClass}`} />
              <Text as="span" size="tiny" className="text-primary-900">
                {item.label} ({item.count})
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const PropertyTypeDistributionWidget: React.FC<{
  response: ReportPropertyTypeDistributionResponse | undefined;
}> = ({ response }) => {
  const { t } = useI18n();
  const rows = response?.rows ?? [];
  if (rows.length === 0) {
    return (
      <EmptyState
        title={t('reports.dashboard.empty.noPropertiesTitle')}
        description={t('reports.dashboard.empty.propertyTypeDistribution')}
        icon={<PiHouseLine className="h-6 w-6" />}
        size="sm"
      />
    );
  }

  const maxCount = Math.max(...rows.map((row) => row.count), 1);

  return (
    <div className="space-y-3">
      {rows.map((row, index) => (
        <div key={row.type} className="space-y-1.5">
          <div className="flex items-center justify-between gap-3 text-sm">
            <Text
              as="span"
              size="small"
              weight="medium"
              className="text-primary-900"
            >
              {row.label}
            </Text>
            <Text as="span" size="small" className="text-primary-900">
              {row.count} · {row.percentage.toFixed(0)}%
            </Text>
          </div>
          <div className="overflow-hidden rounded-full bg-neutral-100">
            <div
              className="h-3 rounded-full"
              style={{
                width: `${(row.count / maxCount) * 100}%`,
                backgroundColor: getSeriesColor(index),
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const PropertyInterestRankingWidget: React.FC<{
  response: ReportPropertyInterestRankingResponse | undefined;
  onOpenProperty: (propertyId: number) => void;
}> = ({ response, onOpenProperty }) => {
  const { t } = useI18n();
  const rows = response?.rows ?? [];
  const { properties, loadingPropertyIds } = useReferencedProperties(
    rows.map((row) => row.propertyId)
  );
  if (rows.length === 0) {
    return (
      <EmptyState
        title={t('reports.dashboard.empty.noRankingTitle')}
        description={t('reports.dashboard.empty.propertyInterestRanking')}
        icon={<PiRows className="h-6 w-6" />}
        size="sm"
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-neutral-100">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-3 bg-neutral-50 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-neutral-500">
        <span>{t('reports.dashboard.table.property')}</span>
        <span>{t('reports.dashboard.table.consultations')}</span>
      </div>
      <div className="divide-y divide-neutral-100">
        {rows.map((row, index) => (
          <button
            key={row.propertyId}
            type="button"
            onClick={() => onOpenProperty(row.propertyId)}
            className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left transition hover:bg-neutral-50"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-900 text-sm font-semibold text-white">
                {index + 1}
              </div>
              <div>
                <Text
                  as="div"
                  size="small"
                  weight="medium"
                  className="text-primary-900"
                >
                  {row.label}
                </Text>
                <Text as="div" size="tiny" className="text-primary-900">
                  <PropertyCode
                    loading={loadingPropertyIds.has(row.propertyId)}
                    displayCode={properties.get(row.propertyId)?.displayCode}
                    fallback={`ID #${row.propertyId}`}
                  />
                </Text>
              </div>
            </div>
            <div className="rounded-full bg-neutral-100 px-2.5 py-1 text-sm font-medium text-primary-900">
              <Text
                as="span"
                size="small"
                weight="medium"
                className="text-primary-900"
              >
                {row.count}
              </Text>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const UserAssignmentRankingWidget: React.FC<{
  response: ReportUserAssignmentRankingResponse | undefined;
}> = ({ response }) => {
  const { t } = useI18n();
  const rows = response?.rows ?? [];
  const statuses = response?.statuses ?? [];
  if (rows.length === 0 || statuses.length === 0) {
    return (
      <EmptyState
        title={t('reports.dashboard.empty.noAssignmentsTitle')}
        description={t('reports.dashboard.empty.userAssignmentRanking')}
        icon={<PiTable className="h-6 w-6" />}
        size="sm"
      />
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-100">
      <table className="min-w-full divide-y divide-neutral-100 text-sm">
        <thead className="bg-neutral-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-primary-900">
              {t('reports.dashboard.table.user')}
            </th>
            {statuses.map((status) => (
              <th
                key={status.id}
                className="px-3 py-2 text-right font-medium text-primary-900"
              >
                <FunnelStatusName
                  name={status.name}
                  outcome={status.terminalOutcome}
                  icon="outcome"
                  weight="medium"
                />
              </th>
            ))}
            <th className="px-3 py-2 text-right font-medium text-primary-900">
              {t('reports.dashboard.table.total')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-neutral-100 bg-white">
          {rows.map((row) => (
            <tr key={row.userId}>
              <td className="px-3 py-2 font-medium text-primary-900">
                {row.label}
              </td>
              {statuses.map((status) => (
                <td
                  key={status.id}
                  className="px-3 py-2 text-right text-primary-900"
                >
                  {row.statuses.find((cell) => cell.statusId === status.id)
                    ?.count ?? 0}
                </td>
              ))}
              <td className="px-3 py-2 text-right font-semibold text-primary-900">
                {row.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
